import { Check, Pencil, Plus, Undo2, X } from "lucide-react";
import * as React from "react";

import {
  FormCorrecao,
  IndicadorGravacao,
  type EstadoGravacao,
} from "@/components/fiscal/CorrecaoFiscal";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { dataBR } from "@/lib/format";
import { cn } from "@/lib/utils";
import type {
  BlocoContabil,
  CampoContabil,
  CorrecaoTax,
  SubstituicoesContabil,
} from "@/types";

/** Correção nova (valor e motivo) ou `null` para voltar ao valor calculado. */
type Mudanca = Pick<CorrecaoTax, "valor" | "motivo"> | null;

/** Aplica de uma vez as mudanças de uma NF — uma linha nova grava quatro campos juntos. */
export type CorrigirContabil = (
  chave: string,
  mudancas: Partial<Record<CampoContabil, Mudanca>>,
) => void;

/** Colunas que o Contábil corrige, na ordem da tabela — NF e Operação ficam de fora. */
const CAMPOS_CONTABEIS: { campo: CampoContabil; rotulo: string }[] = [
  { campo: "evento", rotulo: "Evento SAP / Movimento" },
  { campo: "lancamentos", rotulo: "Lançamentos" },
  { campo: "iva", rotulo: "Código IVA / Tributos" },
  { campo: "obs", rotulo: "Parametrização" },
];

type ParteLancamento = "dc" | "chave" | "conta" | "desc" | "removido";

const ROTULO_PARTE: Record<ParteLancamento, string> = {
  dc: "D/C",
  chave: "Chave",
  conta: "Item FS",
  desc: "Descrição",
  removido: "Removido",
};

/** `lanc2_conta` → { indice: 2, parte: "conta" }: o lançamento e a parte corrigidos. */
function campoLancamento(campo: string) {
  const m = /^lanc(\d+)_(dc|chave|conta|desc|removido)$/.exec(campo);
  return m ? { indice: Number(m[1]), parte: m[2] as ParteLancamento } : null;
}

const textoLancamento = (l: BlocoContabil["lancs"][number]) =>
  `${l.dc} ${l.chave} ${l.conta} — ${l.desc}`;

const PARTES_NOVO = ["dc", "chave", "conta", "desc"] as const;

interface LinhaNovaDados {
  k: number;
  dc: string;
  chave: string;
  conta: string;
  desc: string;
  nf?: string;
  por?: string;
  em?: string;
}

/** Próximo número de linha nova da NF (`novo<K>_…`), sem repetir nenhum já usado. */
function proximoNumeroNovo(campos: SubstituicoesContabil[string] | undefined) {
  const usados = Object.keys(campos ?? {}).flatMap((c) => {
    const m = /^novo(\d+)_/.exec(c);
    return m ? [Number(m[1])] : [];
  });
  return usados.length ? Math.max(...usados) + 1 : 1;
}

/** Linhas acrescentadas pelo Contábil (completas), na ordem em que foram criadas. */
function linhasNovas(campos: SubstituicoesContabil[string] | undefined): LinhaNovaDados[] {
  const porNumero = new Map<number, Partial<Record<(typeof PARTES_NOVO)[number], CorrecaoTax>>>();
  for (const [campo, correcao] of Object.entries(campos ?? {})) {
    const m = /^novo(\d+)_(dc|chave|conta|desc)$/.exec(campo);
    if (!m || !correcao) continue;
    const partes = porNumero.get(Number(m[1])) ?? {};
    partes[m[2] as (typeof PARTES_NOVO)[number]] = correcao;
    porNumero.set(Number(m[1]), partes);
  }
  return [...porNumero.entries()]
    .sort(([a], [b]) => a - b)
    .flatMap(([k, p]) =>
      p.dc && p.chave && p.conta && p.desc
        ? [
            {
              k,
              dc: p.dc.valor,
              chave: p.chave.valor,
              conta: p.conta.valor,
              desc: p.desc.valor,
              nf: p.dc.nf,
              por: p.dc.por,
              em: p.dc.em,
            },
          ]
        : [],
    );
}

const rotuloDe = (campo: CampoContabil) => {
  const lanc = campoLancamento(campo);
  if (lanc) return `Lançamento ${lanc.indice + 1} · ${ROTULO_PARTE[lanc.parte]}`;
  return CAMPOS_CONTABEIS.find((c) => c.campo === campo)?.rotulo ?? campo;
};

/** Correções em lista, para contar e resumir. */
export function listarCorrecoesContabil(subs: SubstituicoesContabil | undefined) {
  return Object.entries(subs ?? {}).flatMap(([chave, campos]) => {
    const itens = (Object.entries(campos ?? {}) as [CampoContabil, CorrecaoTax][])
      .filter(([campo]) => !campo.startsWith("novo"))
      .map(([campo, correcao]) => ({
        chave,
        campo,
        rotulo: rotuloDe(campo),
        correcao: campo.endsWith("_removido") ? { ...correcao, valor: "removido" } : correcao,
      }));
    const novas = linhasNovas(campos).map((n) => ({
      chave,
      campo: `novo${n.k}_desc` as CampoContabil,
      rotulo: "Lançamento adicionado",
      correcao: {
        valor: `${n.dc} ${n.chave} ${n.conta} — ${n.desc}`,
        motivo: "",
        nf: n.nf,
        por: n.por,
        em: n.em,
      } as CorrecaoTax,
    }));
    return [...itens, ...novas];
  });
}

/** Ao clicar, o sentido do lançamento gira entre débito, crédito e nenhum. */
const PROXIMO_SENTIDO: Record<string, string> = { D: "C", C: "-", "-": "D" };

/**
 * Selo D/C do lançamento. Com `onCorrigir`, cada clique troca o sentido (D → C → -);
 * fora do sentido calculado o selo fica âmbar, como as demais correções, e voltar ao
 * calculado desfaz a correção.
 */
function SeloSentido({
  bloco,
  indice,
  correcao,
  onCorrigir,
}: {
  bloco: BlocoContabil;
  indice: number;
  correcao: CorrecaoTax | undefined;
  onCorrigir?: CorrigirContabil;
}) {
  const calculado = bloco.lancs[indice].dc;
  const atual = correcao?.valor ?? calculado;
  const classes = cn(
    "w-5 shrink-0 rounded text-center text-[10px] font-extrabold text-white",
    correcao ? "bg-amber-400" : corChave(atual),
  );
  if (!onCorrigir) {
    return (
      <span
        className={classes}
        title={correcao ? `Corrigido de ${calculado} por ${correcao.por ?? "—"}` : undefined}
      >
        {atual}
      </span>
    );
  }
  const campo: CampoContabil = `lanc${indice}_dc`;
  const proximo = PROXIMO_SENTIDO[atual] ?? "D";
  return (
    <button
      type="button"
      aria-label={`Sentido do lançamento ${indice + 1} da NF ${bloco.ref}: ${atual}. Alternar para ${proximo}`}
      title={correcao ? `Calculado: ${calculado}. Clique para alternar` : "Clique para alternar D/C/-"}
      className={cn(classes, "cursor-pointer transition-opacity hover:opacity-80")}
      onClick={() =>
        onCorrigir(bloco.chave, {
          [campo]: proximo === calculado ? null : { valor: proximo, motivo: correcao?.motivo ?? "" },
        })
      }
    >
      {atual}
    </button>
  );
}

/** Texto calculado da coluna; os lançamentos, um por linha, como a correção os escreve. */
function calculadoDe(bloco: BlocoContabil, campo: CampoContabil): string {
  const lanc = campoLancamento(campo);
  if (lanc) {
    const linha = bloco.lancs[lanc.indice];
    if (!linha) return "";
    return lanc.parte === "removido" ? textoLancamento(linha) : linha[lanc.parte];
  }
  if (campo === "lancamentos") {
    return bloco.lancs.map(textoLancamento).join("\n");
  }
  return bloco[campo as "evento" | "iva" | "obs"];
}

const corChave = (dc: string) =>
  dc === "D" ? "bg-fs-verde" : dc === "C" ? "bg-cyan-700" : "bg-muted-foreground";

/**
 * Chave ou item FS de um lançamento, corrigível como os valores do roteiro fiscal:
 * clicar abre o formulário; corrigido, mostra o valor novo em âmbar com o calculado
 * riscado abaixo.
 */
function ValorLancamento({
  bloco,
  indice,
  parte,
  centralizado = true,
  correcao,
  onCorrigir,
  className,
}: {
  bloco: BlocoContabil;
  indice: number;
  parte: "chave" | "conta" | "desc";
  /** Valores curtos (chave, item FS) ficam centralizados; a descrição, à esquerda. */
  centralizado?: boolean;
  correcao: CorrecaoTax | undefined;
  onCorrigir?: CorrigirContabil;
  className?: string;
}) {
  const [aberto, setAberto] = React.useState(false);
  const campo: CampoContabil = `lanc${indice}_${parte}`;
  const rotulo = `${ROTULO_PARTE[parte]} do lançamento ${indice + 1}`;
  const calculado = correcao?.original ?? bloco.lancs[indice][parte];

  const alinhamento = centralizado ? "items-center text-center" : "items-start text-left";
  // A descrição é texto, como Evento, Código IVA e Parametrização: formulário amplo, já
  // preenchido com o calculado, e só o texto corrigido em âmbar, sem o original riscado.
  const texto = parte === "desc";

  if (!correcao && !onCorrigir) {
    return <span className={cn(className, centralizado && "text-center")}>{calculado}</span>;
  }

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${onCorrigir ? "Corrigir" : "Ver correção de"} ${rotulo} da NF ${bloco.ref}`}
          title={correcao ? `Calculado: ${calculado}` : "Clique para corrigir"}
          className={cn(
            "group/valor relative -my-0.5 rounded-md px-1 py-0.5 leading-tight",
            texto ? "block text-left" : cn("inline-flex flex-col", alinhamento),
            "transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            correcao && "bg-amber-50/60 ring-1 ring-amber-100 hover:bg-amber-50",
            aberto && "bg-secondary",
          )}
        >
          <span
            className={cn(
              className,
              centralizado && "text-center",
              correcao && (texto ? "font-medium text-amber-600" : "font-bold text-amber-600"),
            )}
          >
            {correcao?.valor ?? calculado}
          </span>
          {correcao && !texto && (
            <span className="text-[10px] font-normal text-muted-foreground line-through">
              {calculado}
            </span>
          )}
          {onCorrigir && !correcao && (
            <Pencil
              aria-hidden
              className="nao-imprimir absolute -right-1.5 -top-1.5 size-3 text-muted-foreground opacity-0 transition-opacity group-hover/valor:opacity-100 group-focus-visible/valor:opacity-100"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className={cn("space-y-3 text-left", texto && "w-[min(30rem,92vw)]")}
      >
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            NF {bloco.ref} · {rotulo}
          </p>
          <p className={cn("mt-0.5", texto && onCorrigir && "hidden")}>
            Calculado:{" "}
            <span className={cn("font-extrabold numerico", correcao && "line-through")}>
              {calculado}
            </span>
          </p>
        </div>
        {correcao?.por && (
          <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900">
            Corrigido para <strong>{correcao.valor}</strong> por {correcao.por}
            {correcao.em && ` em ${dataBR(correcao.em)}`}.
            {correcao.motivo && <span className="mt-1 block italic">“{correcao.motivo}”</span>}
          </p>
        )}
        {onCorrigir && (
          <FormCorrecao
            multilinha={texto}
            calculado={calculado}
            rotulo={ROTULO_PARTE[parte]}
            correcao={correcao}
            onCancelar={() => setAberto(false)}
            onRestaurar={() => {
              onCorrigir(bloco.chave, { [campo]: null });
              setAberto(false);
            }}
            onAplicar={(valor, motivo) => {
              onCorrigir(bloco.chave, { [campo]: { valor, motivo } });
              setAberto(false);
            }}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

const BOTAO_DISCRETO =
  "nao-imprimir shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40";

/** Formulário inline de uma linha nova: Enter confirma, Esc cancela, Tab passa de campo. */
function FormLinhaNova({
  inicial,
  onConfirmar,
  onCancelar,
}: {
  inicial?: LinhaNovaDados;
  onConfirmar: (linha: Pick<LinhaNovaDados, "dc" | "chave" | "conta" | "desc">) => void;
  onCancelar: () => void;
}) {
  const [dc, setDc] = React.useState(inicial?.dc ?? "D");
  const [chave, setChave] = React.useState(inicial?.chave ?? "");
  const [conta, setConta] = React.useState(inicial?.conta ?? "");
  const [desc, setDesc] = React.useState(inicial?.desc ?? "");
  const valido = Boolean(chave.trim() && conta.trim() && desc.trim());

  const confirmar = () => {
    if (valido) onConfirmar({ dc, chave: chave.trim(), conta: conta.trim(), desc: desc.trim() });
  };

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-md bg-amber-50/60 p-1 ring-1 ring-amber-100"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          confirmar();
        } else if (e.key === "Escape") {
          e.preventDefault();
          onCancelar();
        }
      }}
    >
      <button
        type="button"
        aria-label={`Sentido: ${dc}. Alternar`}
        title="Clique para alternar D/C/-"
        onClick={() => setDc(PROXIMO_SENTIDO[dc] ?? "D")}
        className="w-5 shrink-0 cursor-pointer rounded bg-amber-400 text-center text-[10px] font-extrabold text-white hover:opacity-80"
      >
        {dc}
      </button>
      <Input
        autoFocus
        aria-label="Chave"
        placeholder="Chave"
        maxLength={40}
        value={chave}
        onChange={(e) => setChave(e.target.value)}
        className="h-7 w-14 px-1.5 text-center text-[11px] numerico"
      />
      <Input
        aria-label="Item FS"
        placeholder="Item FS"
        maxLength={40}
        value={conta}
        onChange={(e) => setConta(e.target.value)}
        className="h-7 w-28 px-1.5 text-center text-[11px] numerico"
      />
      <Input
        aria-label="Descrição"
        placeholder="Descrição"
        maxLength={200}
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        className="h-7 min-w-32 flex-1 px-1.5 text-[11px]"
      />
      <button
        type="button"
        aria-label="Confirmar lançamento"
        title="Confirmar (Enter)"
        disabled={!valido}
        onClick={confirmar}
        className={cn(BOTAO_DISCRETO, "text-emerald-600 disabled:opacity-40")}
      >
        <Check className="size-3.5" />
      </button>
      <button
        type="button"
        aria-label="Cancelar"
        title="Cancelar (Esc)"
        onClick={onCancelar}
        className={BOTAO_DISCRETO}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

const REMOVER_AO_PASSAR_O_MOUSE =
  "ml-auto opacity-0 group-hover/linha:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100";

/**
 * Lançamentos de uma NF: os calculados (com D/C, chave, item FS e descrição corrigíveis),
 * os removidos pelo Contábil (riscados, com "Desfazer") e os acrescentados por ele, sempre
 * ao final. Remover e acrescentar valem só com `onCorrigir`.
 */
function CelulaLancamentos({
  bloco,
  substituicoes,
  onCorrigir,
}: {
  bloco: BlocoContabil;
  substituicoes?: SubstituicoesContabil;
  onCorrigir?: CorrigirContabil;
}) {
  const [adicionando, setAdicionando] = React.useState(false);
  const [editando, setEditando] = React.useState<number | null>(null);
  const campos = substituicoes?.[bloco.chave];
  const textoCorrigido = campos?.lancamentos;
  // Um texto corrigido no lugar de toda a coluna (versão anterior) continua valendo, sem edição.
  const editavel = textoCorrigido ? undefined : onCorrigir;
  const novas = linhasNovas(campos);

  const gravarNova = (k: number, l: Pick<LinhaNovaDados, "dc" | "chave" | "conta" | "desc">) => {
    onCorrigir?.(
      bloco.chave,
      Object.fromEntries(
        PARTES_NOVO.map((p) => [`novo${k}_${p}`, { valor: l[p], motivo: "" }]),
      ) as Partial<Record<CampoContabil, Mudanca>>,
    );
  };

  const linhas = (
    <div className="divide-y divide-border/60">
      {bloco.lancs.map((lanc, j) =>
        campos?.[`lanc${j}_removido`] ? (
          <div
            key={j}
            className="flex items-baseline gap-1.5 py-1 text-[11px] text-muted-foreground first:pt-0 last:pb-0"
          >
            <span className="line-through">{textoLancamento(lanc)}</span>
            <span className="rounded-full bg-amber-50 px-1.5 text-[9px] font-bold uppercase text-amber-600 ring-1 ring-amber-100">
              removido
            </span>
            {editavel && (
              <button
                type="button"
                aria-label={`Desfazer a remoção do lançamento ${j + 1} da NF ${bloco.ref}`}
                onClick={() => editavel(bloco.chave, { [`lanc${j}_removido`]: null })}
                className={cn(
                  BOTAO_DISCRETO,
                  "ml-auto inline-flex items-center gap-1 px-1.5 text-[10px] font-semibold",
                )}
              >
                <Undo2 className="size-3" /> Desfazer
              </button>
            )}
          </div>
        ) : (
          <div
            key={j}
            className="group/linha flex items-baseline gap-1.5 py-1 first:pt-0 last:pb-0"
          >
            <SeloSentido
              bloco={bloco}
              indice={j}
              correcao={campos?.[`lanc${j}_dc`]}
              onCorrigir={editavel}
            />
            <ValorLancamento
              bloco={bloco}
              indice={j}
              parte="chave"
              correcao={campos?.[`lanc${j}_chave`]}
              onCorrigir={editavel}
              className="min-w-6 text-[11px] font-extrabold text-fs-escuro numerico"
            />
            <ValorLancamento
              bloco={bloco}
              indice={j}
              parte="conta"
              correcao={campos?.[`lanc${j}_conta`]}
              onCorrigir={editavel}
              className="min-w-16 text-[11px] font-extrabold text-fs-escuro numerico"
            />
            <ValorLancamento
              bloco={bloco}
              indice={j}
              parte="desc"
              centralizado={false}
              correcao={campos?.[`lanc${j}_desc`]}
              onCorrigir={editavel}
              className="text-[11px]"
            />
            {editavel && (
              <button
                type="button"
                aria-label={`Remover o lançamento ${j + 1} da NF ${bloco.ref}`}
                title="Remover lançamento"
                onClick={() =>
                  editavel(bloco.chave, { [`lanc${j}_removido`]: { valor: "1", motivo: "" } })
                }
                className={cn(BOTAO_DISCRETO, REMOVER_AO_PASSAR_O_MOUSE, "hover:text-destructive")}
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        ),
      )}
      {novas.map((n) =>
        editando === n.k && editavel ? (
          <FormLinhaNova
            key={`novo${n.k}`}
            inicial={n}
            onCancelar={() => setEditando(null)}
            onConfirmar={(l) => {
              gravarNova(n.k, l);
              setEditando(null);
            }}
          />
        ) : (
          <div
            key={`novo${n.k}`}
            className="group/linha flex items-baseline gap-1.5 py-1 first:pt-0 last:pb-0"
          >
            <span className="w-5 shrink-0 rounded bg-amber-400 text-center text-[10px] font-extrabold text-white">
              {n.dc}
            </span>
            {(() => {
              const conteudo = (
                <>
                  <span className="min-w-6 text-center text-[11px] font-bold numerico">{n.chave}</span>
                  <span className="min-w-16 text-center text-[11px] font-bold numerico">{n.conta}</span>
                  <span className="text-[11px] font-medium">{n.desc}</span>
                </>
              );
              const classes = "flex min-w-0 items-baseline gap-1.5 text-amber-600";
              return editavel ? (
                <button
                  type="button"
                  aria-label={`Editar o lançamento novo da NF ${bloco.ref}`}
                  title="Clique para editar"
                  onClick={() => setEditando(n.k)}
                  className={cn(classes, "rounded-md px-1 text-left hover:bg-amber-50")}
                >
                  {conteudo}
                </button>
              ) : (
                <span className={classes} title={n.por ? `Acrescentado por ${n.por}` : undefined}>
                  {conteudo}
                </span>
              );
            })()}
            <span className="rounded-full bg-amber-50 px-1.5 text-[9px] font-bold uppercase text-amber-600 ring-1 ring-amber-100">
              novo
            </span>
            {editavel && (
              <button
                type="button"
                aria-label={`Remover o lançamento novo da NF ${bloco.ref}`}
                title="Remover lançamento"
                onClick={() =>
                  editavel(
                    bloco.chave,
                    Object.fromEntries(PARTES_NOVO.map((p) => [`novo${n.k}_${p}`, null])),
                  )
                }
                className={cn(BOTAO_DISCRETO, REMOVER_AO_PASSAR_O_MOUSE, "hover:text-destructive")}
              >
                <X className="size-3.5" />
              </button>
            )}
          </div>
        ),
      )}
      {adicionando && editavel && (
        <FormLinhaNova
          onCancelar={() => setAdicionando(false)}
          onConfirmar={(l) => {
            gravarNova(proximoNumeroNovo(campos), l);
            setAdicionando(false);
          }}
        />
      )}
    </div>
  );

  if (textoCorrigido) {
    return (
      <CelulaContabil
        bloco={bloco}
        onCorrigir={onCorrigir}
        campo="lancamentos"
        correcao={textoCorrigido}
        className="min-w-72"
      >
        {linhas}
      </CelulaContabil>
    );
  }

  return (
    <TableCell className="min-w-72">
      {linhas}
      {editavel && !adicionando && (
        <button
          type="button"
          onClick={() => setAdicionando(true)}
          className="nao-imprimir mt-1.5 inline-flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-border py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:border-amber-300 hover:bg-amber-50/60 hover:text-amber-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        >
          <Plus className="size-3" /> Adicionar lançamento
        </button>
      )}
    </TableCell>
  );
}

/**
 * Célula de uma coluna que o Contábil pode corrigir. Sem `onCorrigir` e sem
 * correção, mostra só o calculado (`children`); corrigida, abre o detalhe (quem,
 * quando, por quê) mesmo para quem só visualiza.
 */
function CelulaContabil({
  bloco,
  campo,
  correcao,
  onCorrigir,
  className,
  children,
}: {
  bloco: BlocoContabil;
  campo: CampoContabil;
  correcao: CorrecaoTax | undefined;
  onCorrigir?: CorrigirContabil;
  className?: string;
  children: React.ReactNode;
}) {
  const [aberto, setAberto] = React.useState(false);
  const rotulo = rotuloDe(campo);
  const calculado = correcao?.original ?? calculadoDe(bloco, campo);

  if (!correcao && !onCorrigir) {
    return <TableCell className={className}>{children}</TableCell>;
  }

  return (
    <TableCell className={className}>
      <Popover open={aberto} onOpenChange={setAberto}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${onCorrigir ? "Corrigir" : "Ver correção de"} ${rotulo} da NF ${bloco.ref}`}
            title={correcao ? `Corrigido por ${correcao.por ?? "—"}` : "Clique para corrigir"}
            className={cn(
              "group/valor relative -m-1 block w-full rounded-md p-1 text-left",
              "transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
              correcao && "bg-amber-50/60 ring-1 ring-amber-100 hover:bg-amber-50",
              aberto && "bg-secondary",
            )}
          >
            {correcao ? (
              <span className="block whitespace-pre-line font-medium text-amber-600">
                {correcao.valor}
              </span>
            ) : (
              children
            )}
            {onCorrigir && !correcao && (
              <Pencil
                aria-hidden
                className="nao-imprimir absolute -right-1 -top-1 size-3 text-muted-foreground opacity-0 transition-opacity group-hover/valor:opacity-100 group-focus-visible/valor:opacity-100"
              />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[min(30rem,92vw)] space-y-3 text-left">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
            NF {bloco.ref} · {rotulo}
          </p>
          {correcao?.por && (
            <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900">
              Corrigido por {correcao.por}
              {correcao.em && ` em ${dataBR(correcao.em)}`}.
              {correcao.motivo && <span className="mt-1 block italic">“{correcao.motivo}”</span>}
            </p>
          )}
          {onCorrigir ? (
            <FormCorrecao
              multilinha
              rotulo={rotulo}
              correcao={correcao}
              calculado={calculado}
              onCancelar={() => setAberto(false)}
              onRestaurar={() => {
                onCorrigir(bloco.chave, { [campo]: null });
                setAberto(false);
              }}
              onAplicar={(valor, motivo) => {
                onCorrigir(bloco.chave, { [campo]: { valor, motivo } });
                setAberto(false);
              }}
            />
          ) : (
            <p className="whitespace-pre-line text-xs">
              Calculado: <span className="line-through">{calculado}</span>
            </p>
          )}
        </PopoverContent>
      </Popover>
    </TableCell>
  );
}

export function RoteiroContabil({
  blocos,
  substituicoes,
  onCorrigir,
  gravacao = null,
  onTentarDeNovo,
}: {
  blocos: BlocoContabil[];
  /** Correções do Contábil: o texto novo aparece com o calculado riscado abaixo. */
  substituicoes?: SubstituicoesContabil;
  /** Libera a correção das colunas (validação do Contábil): clicar na célula abre o formulário. */
  onCorrigir?: CorrigirContabil;
  /** Estado da gravação das correções, mostrado no cabeçalho. */
  gravacao?: EstadoGravacao;
  onTentarDeNovo?: () => void;
}) {
  const corrigidos = listarCorrecoesContabil(substituicoes).length;

  if (!blocos.length) {
    return (
      <Card>
        <CardContent className="p-5 text-sm text-muted-foreground">
          O roteiro contábil aparece assim que houver notas no roteiro fiscal.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id="roteiro-contabil" className="scroll-mt-4">
      <CardHeader>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle>Roteiro contábil — parametrização SAP S/4HANA</CardTitle>
          {corrigidos > 0 && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800">
              {corrigidos} {corrigidos === 1 ? "valor corrigido" : "valores corrigidos"} pelo
              Contábil
            </span>
          )}
          {gravacao && <IndicadorGravacao estado={gravacao} onTentarDeNovo={onTentarDeNovo} />}
        </div>
        <CardDescription>
          Lançamentos sugeridos por nota, com chaves e eventos SAP. Itens conforme o plano de itens
          FS (hierarquia DF) — validar o de-para final com Contabilidade e TAX.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table className="min-w-[56rem]">
          <TableHeader>
            <TableRow>
              <TableHead>NF</TableHead>
              <TableHead className="text-left">Operação</TableHead>
              <TableHead className="text-left">Evento SAP / Movimento</TableHead>
              <TableHead className="text-left">Lançamentos (chave · item FS · descrição)</TableHead>
              <TableHead className="text-left">Código IVA / Tributos</TableHead>
              <TableHead className="text-left">Parametrização</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {blocos.map((bloco, i) => {
              const celula = { bloco, onCorrigir };
              return (
                <TableRow key={`${bloco.ref}-${i}`}>
                  <TableCell className="p-0">
                    <div className="flex h-full min-h-10 w-8 items-center justify-center bg-fs-verde text-sm font-extrabold text-white">
                      {bloco.ref}
                    </div>
                  </TableCell>
                  <TableCell className="min-w-44">
                    <div className="font-extrabold text-fs-escuro">{bloco.operacao}</div>
                    <div className="text-[11px] text-muted-foreground">{bloco.rota}</div>
                  </TableCell>
                  <CelulaContabil
                    {...celula}
                    campo="evento"
                    correcao={substituicoes?.[bloco.chave]?.evento}
                    className="min-w-44 text-[11px]"
                  >
                    {bloco.evento}
                  </CelulaContabil>
                  <CelulaLancamentos
                    bloco={bloco}
                    substituicoes={substituicoes}
                    onCorrigir={onCorrigir}
                  />
                  <CelulaContabil
                    {...celula}
                    campo="iva"
                    correcao={substituicoes?.[bloco.chave]?.iva}
                    className="min-w-40 text-[11px]"
                  >
                    {bloco.iva}
                  </CelulaContabil>
                  <CelulaContabil
                    {...celula}
                    campo="obs"
                    correcao={substituicoes?.[bloco.chave]?.obs}
                    className="min-w-48 text-[11px]"
                  >
                    {bloco.obs}
                  </CelulaContabil>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Chaves: 01/11 cliente · 31/21 fornecedor · 40/50 razão · 81/91 custo · 86/96 GR/IR · 89/99
          estoque. Transações de apoio: OBYC, VKOA, OB09/KDF, J1BTAX, J1B1N.
        </p>
      </CardContent>
    </Card>
  );
}
